import React, { useState } from 'react';
import { CheckCircle, CaretRight, Car, MapPin, UserCircle } from '@phosphor-icons/react';
import { StatusSelect, DealSelect, LocationSelect, TextEdit, KeysSelect, SalespersonSelect } from './EditableCell';
import { BRAND_LOGO } from './Badges';
import {
  STAGE_COLS, STAGE_LABELS, stageProgress,
  canEditField, todayISO, formatTime, titleCase, brandLabel,
} from '../helpers';
import { api } from '../api';
import { showToast } from './Toast';

/**
 * Full-bleed vehicle row — replaces the 12-column table per the design handoff.
 * Identity · delivery · inline stage pipeline · keys · progress. Everything the
 * old row could edit inline still edits inline.
 *
 * Urgent = delivering today with nothing started at all. Anything looser and
 * every row on a busy morning turns red, which is what the review caught.
 */
export default function FlowRow({ vehicle, onUpdate, onChanged, role }) {
  const v = vehicle;
  const [local, setLocal] = useState(v);
  React.useEffect(() => { setLocal(v); }, [v]);

  const can = (field) => canEditField(role, field);
  const isToday = String(local.delivery_date || '').slice(0, 10) === todayISO();
  const pct = stageProgress(local);
  const ready = pct === 100;
  const applicable = STAGE_COLS.map((c) => local[c]).filter((s) => s !== 'na');
  const urgent = isToday && applicable.length > 0 && applicable.every((s) => s === 'ns');

  async function save(field, value) {
    if (!can(field)) return showToast('You do not have permission to edit this field');
    const prev = local[field];
    setLocal((s) => ({ ...s, [field]: value }));
    try {
      await api.updateVehicle(v.id, { [field]: value });
      onChanged?.();
    } catch (e) {
      setLocal((s) => ({ ...s, [field]: prev }));
      showToast('Failed: ' + e.message);
    }
  }

  const tone = ready ? 'full' : pct > 0 ? 'some' : 'none';
  const logo = BRAND_LOGO[local.brand];
  // USED CARS / SERVICE have no manufacturer mark — the logo slot keeps a
  // neutral glyph and the department moves down to the tag row.
  const dept = logo ? null : brandLabel(local.brand);

  return (
    <div className={`frow ${urgent ? 'urgent' : ''}`}>
      {/* 1 — Identity */}
      <div className="frow-id">
        {logo
          ? <img className="blogo" src={logo} alt={local.brand} />
          : <span className="blogo blogo-none"><Car size={26} weight="duotone" /></span>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="frow-model">
            <span className="frow-name">{local.model}</span>
            <span className={`frow-colour ${(local.colour || '').toLowerCase().replace(/\s+/g, '')}`}>
              <TextEdit
                value={titleCase(local.colour)}
                placeholder="Colour"
                onChange={(val) => save('colour', val)}
                disabled={!can('colour')}
              />
            </span>
          </div>
          <div className="frow-meta">
            <DealSelect value={local.deal_status} onChange={(val) => save('deal_status', val)} disabled={!can('deal_status')} />
            <span className="num frow-stock" onClick={() => onUpdate(v.id)}>{local.stock_number}</span>
            <span className={`frow-loctag ${local.location || ''}`}>
              <MapPin size={11} weight="fill" />
              <LocationSelect value={local.location} onChange={(val) => save('location', val)} disabled={!can('location')} />
            </span>
            <span className="frow-sales" title={local.salesperson || 'Salesperson'}>
              <UserCircle size={12} weight="duotone" />
              <SalespersonSelect
                value={local.salesperson}
                onChange={(val) => save('salesperson', val)}
                disabled={!can('salesperson')}
                className="sales-pick"
              />
            </span>
            {dept && <span className="frow-dept">{dept}</span>}
          </div>
        </div>
      </div>

      {/* 2 — Delivery */}
      <div className="frow-when" onClick={() => onUpdate(v.id)}>
        <div className={`num frow-time ${urgent ? 'urgent' : ''}`}>{formatTime(local.delivery_time) || '—'}</div>
        {isToday && <span className={`frow-day ${urgent ? 'urgent' : ''}`}>TODAY</span>}
      </div>

      {/* 3 — Pipeline + keys */}
      <div className="frow-pipe">
        {STAGE_COLS.map((col, i) => (
          <div className="fstage" key={col}>
            <div className="fstage-lbl">{STAGE_LABELS[i]}</div>
            <StatusSelect value={local[col]} onChange={(val) => save(col, val)} disabled={!can(col)} />
          </div>
        ))}
        <div className="frow-pipe-div" />
        <div className="fstage">
          <div className="fstage-lbl">KEYS</div>
          <KeysSelect
            value={local.keys_status}
            onChange={(val) => save('keys_status', val)}
            disabled={!can('keys_status')}
          />
        </div>
      </div>

      {/* 4 — Progress + open */}
      <div className="frow-prog">
        <div className="frow-prog-meter">
          <div className={`num frow-pct ${tone}`}>{pct}%</div>
          <div className="frow-bar"><div className={`frow-bar-fill ${tone}`} style={{ width: `${pct}%` }} /></div>
        </div>
        <button
          className={`frow-go ${ready ? 'ready' : ''}`}
          onClick={() => onUpdate(v.id)}
          title={ready ? 'Ready — open checklist' : 'Open vehicle'}
        >
          {ready ? <CheckCircle size={19} weight="fill" /> : <CaretRight size={18} weight="bold" />}
        </button>
      </div>
    </div>
  );
}
